import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, Stack, Button } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { useTemplateDetailsContext } from '../../../_shared/Details/context';
import useAssignedModules from '../../../_shared/_data_sources/useAssignedModules';
import datetimeHelper from 'helpers/datetimeHelper';
import DetailList from '_lex/_atoms/DetailList';
import { AppContext } from 'views/App/context';

export default function TemplateDetails() {
  const { currentOrganizationId, hasPermission } = useContext(AppContext);
  const { template, isDraft } = useTemplateDetailsContext();
  const navigate = useNavigate();

  const assignedModulesQuery = useAssignedModules();
  const canManageTemplates = hasPermission('manageFieldBank', currentOrganizationId || '');

  const getAssignedModuleName = (moduleId: number) => {
    const module = assignedModulesQuery.data?.find((m: any) => m.id === moduleId);
    return module?.name || `Module ${moduleId}`;
  };

  const formatDateOnly = (dateString: string | null | undefined) => {
    if (!dateString) return '--';
    return datetimeHelper.formatDate(dateString);
  };

  const normalizeTemplateCondition = (condition: string | null | undefined): string => {
    if (!condition) return 'inSync';
    return condition.charAt(0).toLowerCase() + condition.slice(1);
  };

  const handleEditDetails = () => {
    const editUrl = isDraft ? 'edit?isDraft=true' : 'edit';
    navigate(editUrl);
  };

  const templateDetailsItems = [
    {
      label: t('performanceReporting.templates.details.fields.name'),
      value: template?.name || '--',
    },
    {
      label: t('performanceReporting.templates.details.fields.assignedModule'),
      value: template?.assignedModuleId ? getAssignedModuleName(template.assignedModuleId) : '--',
    },
    {
      label: t('performanceReporting.templates.details.fields.lastPublished'),
      value: template?.lastPublishDate ? formatDateOnly(template?.lastPublishDate) : '--',
    },
    {
      label: t('performanceReporting.templates.details.fields.author'),
      value: template?.createdBy || '--',
    },
    {
      label: t('performanceReporting.templates.details.fields.description'),
      value: template?.description || '--',
    },
    {
      label: t('performanceReporting.templates.details.fields.templateCondition'),
      value: template?.templateCondition
        ? t(
            `performanceReporting.templates.list.templateCondition.${normalizeTemplateCondition(template.templateCondition)}`
          )
        : '--',
    },
    {
      label: t('performanceReporting.templates.details.fields.reviewWorkflow'),
      value:
        template?.reviewWorkflows
          ?.map((w) => w.name)
          .filter(Boolean)
          .join(', ') || '--',
    },
  ];

  return (
    <Stack spacing={12}>
      <Card className='lex-card'>
        <CardHeader
          title={t('performanceReporting.templates.details.sections.templateDetails')}
          action={
            canManageTemplates ? (
              <Button
                variant='contained'
                size='small'
                color='primary'
                onClick={handleEditDetails}
              >
                {t('performanceReporting.templates.details.actions.edit')}
              </Button>
            ) : null
          }
        />
        <CardContent>
          <DetailList items={templateDetailsItems} />
        </CardContent>
      </Card>
    </Stack>
  );
}
